import type { CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { privateEventReturnState } from '../../../private-events/private-event-return';
import type { WorkshopChip } from '../../primitives/workshopsCalendarTypes';
import { adminChipDetailUrl } from '../../workshopDetailUrlFor';

/** The dashed note a region's workshops block shows in place of a list. */
export const NOTE: CSSProperties = {
  padding: 14,
  borderRadius: 10,
  background: 'var(--nb-bg)',
  border: '1px dashed var(--nb-border)',
  fontSize: 12.5,
  color: 'var(--nb-subtle)',
};

/**
 * Opens a clicked workshop in admin mode, the only mode Overview is reachable
 * from. A private event opens on its own page, which links back here.
 */
export function useOpenWorkshop(): (chip: { id: string; isPrivate?: boolean }) => void {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  return (chip) =>
    navigate(adminChipDetailUrl(chip), {
      state: privateEventReturnState(`${pathname}${search}`, 'Workshops calendar'),
    });
}

/**
 * Drops the sub-region label from workshops in the region being viewed, so a
 * row on the New England page does not repeat "New England". Rows in a
 * sub-region beneath it keep theirs.
 *
 * `regionSlug` and the page's own path segment are produced by the same slug
 * function on the server, so they match by construction. A mismatch would
 * leave the label in place, which is the harmless direction.
 */
export function withoutViewedRegionLabel(
  workshops: WorkshopChip[],
  viewedRegionSlug: string | undefined,
): WorkshopChip[] {
  if (!viewedRegionSlug) return workshops;
  return workshops.map((w) =>
    w.regionSlug === viewedRegionSlug ? { ...w, subregionLabel: undefined } : w,
  );
}
